import { GraphQLService } from '@affine/core/modules/cloud';
import { AccessibleFolderCacheStore } from '@affine/core/modules/organize';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import {
  type AssignMailSignatureInput,
  assignMailSignatureMutation,
  type ConnectMailAccountInput,
  connectMailAccountMutation,
  deleteMailAccountMutation,
  deleteMailAutoresponderOverrideMutation,
  deleteMailRuleMutation,
  deleteMailSignatureAssignmentMutation,
  deleteMailSignatureMutation,
  mailAccountsQuery,
  mailAdminOverviewQuery,
  mailAutorespondersQuery,
  mailboxRootQuery,
  type MailCredentialShareProposalInput,
  mailCredentialShareProposalsQuery,
  mailCurrentUserQuotaQuery,
  type MailFolder,
  mailFoldersQuery,
  mailMessageBodyQuery,
  mailMessagesQuery,
  mailRulesQuery,
  type MailSearchDiagnosticsQuery,
  mailSearchDiagnosticsQuery,
  type MailSearchReindexInput,
  mailSignatureAssignmentsQuery,
  type MailSignatureScope,
  mailSignaturesQuery,
  mailSyncStatesQuery,
  mailTrustedSendersQuery,
  type MailVirtualFolder,
  mailVirtualFoldersQuery,
  type MailWatchInterest,
  type PrepareMailCompositionInput,
  prepareMailCompositionQuery,
  type RebuildMailSearchIndexMutation,
  rebuildMailSearchIndexMutation,
  type ReindexMailSearchMutation,
  reindexMailSearchMutation,
  resetMailCacheUserOverrideMutation,
  type SaveMailDraftInput,
  saveMailDraftMutation,
  type SendMailInput,
  type SendSavedMailDraftInput,
  sendSavedMailDraftMutation,
  setMailWatchInterestMutation,
  syncMailAccountMutation,
  trustMailSenderMutation,
  type UpdateMailAccountInput,
  updateMailAccountMutation,
  type UpdateMailCacheUserOverrideInput,
  updateMailCacheUserOverrideMutation,
  type UpdateMailFolderInput,
  type UpdateMailMessageStateInput,
  type UpdateMailPolicyInput,
  updateMailPolicyMutation,
  type UpsertMailAutoresponderInput,
  upsertMailAutoresponderMutation,
  type UpsertMailRuleInput,
  upsertMailRuleMutation,
  type UpsertMailSignatureInput,
  upsertMailSignatureMutation,
} from '@affine/graphql';
import type { MailChangedEventV1 } from '@streamlain/mail-provider-contract';
import { useLiveData, useService } from '@toeverything/infra';
import type { GraphQLError } from 'graphql';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SWRResponse } from 'swr';
import useSWR from 'swr';

import type {
  MailCreateFolderMutationPayload,
  OfflineMutationRecord,
} from '../../offline-mutation-outbox';
import {
  useWorkspaceLocalCache,
  workspaceLocalCacheScopeKey,
  WorkspaceLocalCacheService,
} from '../../workspace-local-cache';
import { requestAppResourceJson } from '../native-app/app-resource-http';
import { MailMutationOutboxService } from './mail-mutation-outbox';
import {
  applyMailFolderMutationProjection,
  applyMailMessageMutationProjection,
  latestMailFolderMutationStates,
  latestMailMessageMutationStates,
  type MailMutationProjection,
  type MailMutationSyncState,
  projectMailMutationRecords,
} from './mail-mutation-projection';
import {
  broadcastMailAccountsChanged,
  MAIL_CHANGED_BROWSER_EVENT,
  MAIL_RECONNECTED_BROWSER_EVENT,
  subscribeMailAccountsChanged,
} from './mail-realtime-events';
import { deleteMailRemoteImageCacheEntriesForWorkspace } from './mail-remote-image-cache';
import type {
  MailAccountInfo,
  MailAdminOverviewInfo,
  MailAutoresponderInfo,
  MailboxScope,
  MailCredentialShareProposalInfo,
  MailMailboxFolderInfo,
  MailMailboxRootInfo,
  MailMessageBodyInfo,
  MailMessageInfo,
  MailRuleInfo,
  MailSignatureAssignmentInfo,
  MailSignatureInfo,
  MailSyncStateInfo,
  MailTrustedSenderInfo,
  MailUserQuotaInfo,
  MailVirtualFolderInfo,
  PreparedMailCompositionInfo,
} from './types';

const MAIL_POST_SYNC_REFRESH_DELAYS_MS = [2_000, 8_000, 20_000, 45_000];
const MAIL_FOLDER_PROVIDER_CONFIRMATION_TIMEOUT_MS = 30_000;

type MailSearchDiagnosticsInfo =
  MailSearchDiagnosticsQuery['workspace']['mailSearchDiagnostics'];
type MailSearchReindexResultInfo =
  ReindexMailSearchMutation['reindexMailSearch'];
type RebuildMailSearchIndexInfo =
  RebuildMailSearchIndexMutation['rebuildMailSearchIndex'];

export type PendingMailFolderCreateInfo = {
  id: string;
  accountId: string;
  displayName: string;
  parentFolderId: string | null;
  path: string;
  delimiter: string;
  syncState: MailMutationSyncState | 'confirming';
};

function optimisticFolderCreateInfo(
  id: string,
  input: MailCreateFolderMutationPayload['input'],
  folders: MailMailboxFolderInfo[] | undefined,
  syncState: PendingMailFolderCreateInfo['syncState'] = 'pending'
): PendingMailFolderCreateInfo {
  const parentFolder = input.parentFolderId
    ? folders?.find(folder => folder.id === input.parentFolderId)
    : undefined;
  const delimiter = parentFolder?.delimiter || '/';
  const path = parentFolder
    ? `${parentFolder.path}${delimiter}${input.displayName}`
    : input.path?.trim() || input.displayName;
  return {
    id,
    accountId: input.accountId,
    displayName: input.displayName,
    parentFolderId: input.parentFolderId ?? null,
    path,
    delimiter,
    syncState,
  };
}

export type MailOutboxMutationKind =
  | 'send'
  | 'createFolder'
  | 'updateFolder'
  | 'deleteFolder'
  | 'moveMessages'
  | 'updateMessage';

export type MailOutboxMutationInfo = MailMutationProjection & {
  kind: MailOutboxMutationKind;
};

export type PendingMailMutationInfo = MailOutboxMutationInfo;
export type FailedMailMutationInfo = MailOutboxMutationInfo;

export type MailManagedCredentialGrantInput = {
  accountId: string;
  accessDocId?: string | null;
  role: string;
  targetId: string;
  targetType: string;
};

export type MailRevokeManagedCredentialGrantInput = {
  accountId: string;
  targetId: string;
  targetType: string;
};

export type MailManagedCredentialGrantInfo = {
  credentialId: string;
  id: string;
  role: string;
  targetDisplayName: string;
  targetId: string;
  targetSecondaryLabel: string | null;
  targetType: 'group' | 'user';
};

export type MailSetMailboxScopeInput = {
  accountId: string;
  areaRootFolderId?: string | null;
  scope: MailboxScope;
};

type MailMessagesFetcherGraphQL = Pick<GraphQLService, 'gql'>;

function buildMailboxAppResourceUrl(
  workspaceId: string,
  accountId: string,
  suffix: '/credential-grants' | '/credential-grants/revoke' | '/scope'
) {
  return `/api/workspace/${encodeURIComponent(
    workspaceId
  )}/mail/app-resources/accounts/${encodeURIComponent(accountId)}${suffix}`;
}

function mailOutboxMutationInfo(
  projection: MailMutationProjection
): MailOutboxMutationInfo {
  const kind: MailOutboxMutationKind =
    projection.operation === 'mail.send'
      ? 'send'
      : projection.operation === 'mail.createFolder'
        ? 'createFolder'
        : projection.operation === 'mail.updateFolder'
          ? 'updateFolder'
          : projection.operation === 'mail.deleteFolder'
            ? 'deleteFolder'
            : projection.operation === 'mail.moveMessages'
              ? 'moveMessages'
              : 'updateMessage';
  return { ...projection, kind };
}

export async function fetchMailMessages(
  graphql: MailMessagesFetcherGraphQL,
  input: {
    workspaceId: string;
    accountId: string;
    folder: MailFolder;
    folderId: string | null;
    virtualFolder: MailVirtualFolder | null;
    messageLimit: number;
    normalizedMessageSearch: string;
  }
): Promise<MailMessageInfo[]> {
  const response = await graphql.gql({
    query: mailMessagesQuery,
    variables: {
      workspaceId: input.workspaceId,
      accountId: input.accountId,
      folder: input.folder,
      folderId: input.folderId,
      virtualFolder: input.virtualFolder,
      limit: input.messageLimit,
      search: input.normalizedMessageSearch || null,
    },
  });
  return response.mailMessages;
}

async function fetchMailAccountMessages(
  graphql: MailMessagesFetcherGraphQL,
  input: { workspaceId: string; accountId: string }
): Promise<MailMessageInfo[]> {
  const response = await graphql.gql({
    query: mailMessagesQuery,
    variables: {
      workspaceId: input.workspaceId,
      accountId: input.accountId,
      folder: null,
      folderId: null,
      virtualFolder: null,
      limit: 500,
      cursor: null,
      search: null,
    },
  });
  return response.mailMessages;
}

export function getMailMessagesLocalCacheSegment(input: {
  accountId: string | null;
  folder: MailFolder;
  folderId: string | null;
  virtualFolder: MailVirtualFolder | null;
  limit: number;
  search: string;
}): string {
  return [
    'messages',
    input.accountId ?? 'none',
    input.virtualFolder
      ? `virtual:${input.virtualFolder}`
      : (input.folderId ?? input.folder),
    String(input.limit),
    input.search.trim().toLowerCase(),
  ]
    .map(value => encodeURIComponent(value))
    .join(':');
}

export function getMailMessageBodyLocalCacheSegment(
  messageId: string | null
): string {
  return `message-body:${encodeURIComponent(messageId ?? 'none')}`;
}

type MailMessageSelectionInput = {
  accountId: string | null;
  folder: MailFolder;
  folderId: string | null;
  virtualFolder: MailVirtualFolder | null;
};

export function mailMessageMatchesSelection<
  T extends Pick<
    MailMessageInfo,
    'accountId' | 'folder' | 'folderId' | 'isStarred' | 'isFlagged'
  >,
>(message: T, input: MailMessageSelectionInput): boolean {
  const expectedAccountId = input.accountId?.trim() || null;
  const expectedFolderId = input.folderId?.trim() || null;
  if (expectedAccountId && message.accountId !== expectedAccountId) {
    return false;
  }
  if (input.virtualFolder === 'starred') {
    return message.isStarred;
  }
  if (input.virtualFolder === 'flagged') {
    return message.isFlagged;
  }
  if (expectedFolderId) {
    return message.folderId === expectedFolderId;
  }
  return message.folder === input.folder;
}

export function filterMailMessagesForSelection<
  T extends Pick<
    MailMessageInfo,
    'accountId' | 'folder' | 'folderId' | 'isStarred' | 'isFlagged'
  >,
>(
  input: { messages: T[] | undefined } & MailMessageSelectionInput
): T[] | undefined {
  if (!input.messages) return undefined;
  return input.messages.filter(message =>
    mailMessageMatchesSelection(message, input)
  );
}

export function getMailMessagesForSelection<
  T extends Pick<
    MailMessageInfo,
    'accountId' | 'folder' | 'folderId' | 'isStarred' | 'isFlagged'
  >,
>(input: {
  messages: T[] | undefined;
  accountId: string | null;
  folder: MailFolder;
  folderId: string | null;
  virtualFolder: MailVirtualFolder | null;
}): T[] | undefined {
  if (!input.messages) return undefined;
  const matchesSelection = input.messages.every(message =>
    mailMessageMatchesSelection(message, input)
  );
  return matchesSelection ? input.messages : undefined;
}

function patchMailMessages(
  current: MailMessageInfo[] | undefined,
  patch: UpdateMailMessageStateInput,
  selection?: MailMessageSelectionInput
): MailMessageInfo[] | undefined {
  if (!current) return current;
  let changed = false;
  const next = current.map(message => {
    if (message.id !== patch.messageId) return message;
    changed = true;
    return {
      ...message,
      ...(typeof patch.isRead === 'boolean' ? { isRead: patch.isRead } : {}),
      ...(typeof patch.isFlagged === 'boolean'
        ? { isFlagged: patch.isFlagged }
        : {}),
      ...(typeof patch.isStarred === 'boolean'
        ? { isStarred: patch.isStarred }
        : {}),
      updatedAt: new Date().toISOString(),
    };
  });
  if (!changed) return current;
  return selection
    ? filterMailMessagesForSelection({ messages: next, ...selection })
    : next;
}

function patchMailVirtualFolderCounts(
  current: MailVirtualFolderInfo[] | undefined,
  previousMessage: MailMessageInfo | undefined,
  patch: UpdateMailMessageStateInput
): MailVirtualFolderInfo[] | undefined {
  if (!current || !previousMessage) return current;
  const nextStarred =
    typeof patch.isStarred === 'boolean'
      ? patch.isStarred
      : previousMessage.isStarred;
  const nextFlagged =
    typeof patch.isFlagged === 'boolean'
      ? patch.isFlagged
      : previousMessage.isFlagged;
  if (
    nextStarred === previousMessage.isStarred &&
    nextFlagged === previousMessage.isFlagged
  ) {
    return current;
  }
  return current.map(folderSummary => {
    if (folderSummary.accountId !== previousMessage.accountId) {
      return folderSummary;
    }
    const delta =
      folderSummary.id === 'starred'
        ? Number(nextStarred) - Number(previousMessage.isStarred)
        : folderSummary.id === 'flagged'
          ? Number(nextFlagged) - Number(previousMessage.isFlagged)
          : 0;
    if (!delta) return folderSummary;
    const unreadDelta = previousMessage.isRead ? 0 : delta;
    return {
      ...folderSummary,
      messageCount: Math.max(0, folderSummary.messageCount + delta),
      unreadCount: Math.max(0, folderSummary.unreadCount + unreadDelta),
    };
  });
}

export interface UseWorkspaceMailResult {
  accounts: MailAccountInfo[] | undefined;
  accountsLoading: boolean;
  accountsError: GraphQLError | undefined;
  messages: MailMessageInfo[] | undefined;
  messagesLoading: boolean;
  messagesError: GraphQLError | undefined;
  accountMessages: MailMessageInfo[] | undefined;
  accountMessagesLoading: boolean;
  accountMessagesError: GraphQLError | undefined;
  virtualFolders: MailVirtualFolderInfo[] | undefined;
  virtualFoldersLoading: boolean;
  virtualFoldersError: GraphQLError | undefined;
  folders: MailMailboxFolderInfo[] | undefined;
  foldersLoading: boolean;
  foldersError: GraphQLError | undefined;
  syncStates: MailSyncStateInfo[] | undefined;
  syncStatesLoading: boolean;
  syncStatesError: GraphQLError | undefined;
  trustedSenders: MailTrustedSenderInfo[] | undefined;
  trustedSendersLoading: boolean;
  trustedSendersError: GraphQLError | undefined;
  rules: MailRuleInfo[] | undefined;
  rulesLoading: boolean;
  rulesError: GraphQLError | undefined;
  signatures: MailSignatureInfo[] | undefined;
  signaturesLoading: boolean;
  signaturesError: GraphQLError | undefined;
  signatureAssignments: MailSignatureAssignmentInfo[] | undefined;
  signatureAssignmentsLoading: boolean;
  signatureAssignmentsError: GraphQLError | undefined;
  autoresponders: MailAutoresponderInfo[] | undefined;
  autorespondersLoading: boolean;
  autorespondersError: GraphQLError | undefined;
  selectedBody: MailMessageBodyInfo | undefined;
  selectedBodyMissing: boolean;
  selectedBodyLoading: boolean;
  selectedBodyError: GraphQLError | undefined;
  mailboxRoot: MailMailboxRootInfo | undefined;
  mailboxRootLoading: boolean;
  mailboxRootError: GraphQLError | undefined;
  pendingFolderCreates: PendingMailFolderCreateInfo[];
  pendingMailMutations: PendingMailMutationInfo[];
  failedMailMutations: FailedMailMutationInfo[];
  folderMutationStates: ReadonlyMap<string, MailOutboxMutationInfo>;
  messageMutationStates: ReadonlyMap<string, MailOutboxMutationInfo>;
  refreshAccounts: SWRResponse<MailAccountInfo[], GraphQLError>['mutate'];
  refreshMessages: SWRResponse<MailMessageInfo[], GraphQLError>['mutate'];
  refreshAccountMessages: SWRResponse<
    MailMessageInfo[],
    GraphQLError
  >['mutate'];
  refreshFolders: SWRResponse<MailMailboxFolderInfo[], GraphQLError>['mutate'];
  refreshSyncStates: SWRResponse<MailSyncStateInfo[], GraphQLError>['mutate'];
  refreshTrustedSenders: SWRResponse<
    MailTrustedSenderInfo[],
    GraphQLError
  >['mutate'];
  refreshVirtualFolders: SWRResponse<
    MailVirtualFolderInfo[],
    GraphQLError
  >['mutate'];
  refreshRules: SWRResponse<MailRuleInfo[], GraphQLError>['mutate'];
  refreshSignatures: SWRResponse<MailSignatureInfo[], GraphQLError>['mutate'];
  refreshSignatureAssignments: SWRResponse<
    MailSignatureAssignmentInfo[],
    GraphQLError
  >['mutate'];
  refreshAutoresponders: SWRResponse<
    MailAutoresponderInfo[],
    GraphQLError
  >['mutate'];
  refreshBody: SWRResponse<MailMessageBodyInfo | null, GraphQLError>['mutate'];
  connectAccount: (input: ConnectMailAccountInput) => Promise<MailAccountInfo>;
  updateAccount: (input: UpdateMailAccountInput) => Promise<MailAccountInfo>;
  deleteAccount: (accountId: string) => Promise<void>;
  syncAccount: (accountId: string) => Promise<string>;
  setWatchInterest: (
    accountId: string,
    interest: MailWatchInterest
  ) => Promise<void>;
  credentialShareProposals: (
    input: MailCredentialShareProposalInput
  ) => Promise<MailCredentialShareProposalInfo[]>;
  applyManagedCredentialGrant: (
    input: MailManagedCredentialGrantInput
  ) => Promise<void>;
  listManagedCredentialGrants: (
    accountId: string
  ) => Promise<MailManagedCredentialGrantInfo[]>;
  revokeManagedCredentialGrant: (
    input: MailRevokeManagedCredentialGrantInput
  ) => Promise<void>;
  setMailboxScope: (input: MailSetMailboxScopeInput) => Promise<void>;
  sendMail: (input: SendMailInput) => Promise<void>;
  saveDraft: (input: SaveMailDraftInput) => Promise<{
    accountId: string;
    draftMessageId: string;
    rowId: string;
    providerDraftUid?: string | null;
    savedAt: string;
  }>;
  sendSavedDraft: (input: SendSavedMailDraftInput) => Promise<void>;
  prepareComposition: (
    input: PrepareMailCompositionInput
  ) => Promise<PreparedMailCompositionInfo>;
  upsertSignature: (
    input: UpsertMailSignatureInput
  ) => Promise<MailSignatureInfo>;
  deleteSignature: (signatureId: string) => Promise<void>;
  assignSignature: (
    input: AssignMailSignatureInput
  ) => Promise<MailSignatureAssignmentInfo>;
  deleteSignatureAssignment: (
    accountId: string,
    scope: MailSignatureScope
  ) => Promise<void>;
  upsertAutoresponder: (
    input: UpsertMailAutoresponderInput
  ) => Promise<MailAutoresponderInfo>;
  deleteAutoresponderOverride: (accountId: string) => Promise<void>;
  createFolder: (input: {
    workspaceId: string;
    accountId: string;
    displayName: string;
    parentFolderId?: string | null;
    path?: string | null;
    parentPath?: string | null;
  }) => Promise<void>;
  updateFolder: (input: UpdateMailFolderInput) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  updateMessageState: (input: UpdateMailMessageStateInput) => Promise<void>;
  trustSender: (
    senderAddress: string
  ) => Promise<MailTrustedSenderInfo | undefined>;
  moveMessages: (messageIds: string[], folderId: string) => Promise<void>;
  moveMessage: (messageId: string, folderId: string) => Promise<void>;
  retryFailedMailMutation: (id: string) => Promise<void>;
  dismissFailedMailMutation: (id: string) => Promise<void>;
  upsertRule: (input: UpsertMailRuleInput) => Promise<void>;
  deleteRule: (ruleId: string) => Promise<void>;
}

export function useWorkspaceMailAccounts(workspaceId: string | null) {
  const graphql = useService(GraphQLService);
  return useSWR<MailAccountInfo[], GraphQLError>(
    workspaceId ? ['mailAccounts', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailAccountsQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return response.mailAccounts;
    },
    { suspense: false }
  );
}

export function useWorkspaceMail({
  workspaceId,
  accountId,
  folder,
  folderId,
  virtualFolder,
  selectedMessageId,
  messageLimit = 50,
  messageSearch = '',
  loadMessages = true,
}: {
  workspaceId: string | null;
  accountId: string | null;
  folder: MailFolder;
  folderId: string | null;
  virtualFolder: MailVirtualFolder | null;
  selectedMessageId: string | null;
  messageLimit?: number;
  messageSearch?: string;
  loadMessages?: boolean;
}): UseWorkspaceMailResult {
  const graphql = useService(GraphQLService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const mailMutationOutbox = useService(MailMutationOutboxService);
  const workspaceLocalCache = useService(WorkspaceLocalCacheService);
  const workspaceCacheScope = useLiveData(workspaceLocalCache.scope$);
  const workspaceCacheIdentity =
    workspaceLocalCacheScopeKey(workspaceCacheScope);
  const pendingMailMutationSource = useLiveData(
    mailMutationOutbox.pendingMutations$
  );
  const failedMailMutationSource = useLiveData(
    mailMutationOutbox.failedMutations$
  );
  const pendingMailMutationRecords = useMemo(
    () => pendingMailMutationSource ?? [],
    [pendingMailMutationSource]
  );
  const failedMailMutationRecords = useMemo(
    () => failedMailMutationSource ?? [],
    [failedMailMutationSource]
  );
  const mailMutationRecords = useMemo(
    () => [...pendingMailMutationRecords, ...failedMailMutationRecords],
    [failedMailMutationRecords, pendingMailMutationRecords]
  );
  const mailMutationProjections = useMemo(
    () =>
      projectMailMutationRecords({
        failed: failedMailMutationRecords,
        pending: pendingMailMutationRecords,
        workspaceId,
      }),
    [failedMailMutationRecords, pendingMailMutationRecords, workspaceId]
  );
  const [optimisticFolderCreates, setOptimisticFolderCreates] = useState<
    PendingMailFolderCreateInfo[]
  >([]);
  const normalizedMessageSearch = messageSearch.trim();
  const hasMatchingWorkspaceCacheScope =
    Boolean(workspaceId) && workspaceCacheScope?.workspaceId === workspaceId;
  const localCacheEnabled =
    hasMatchingWorkspaceCacheScope && Boolean(workspaceCacheIdentity);

  const accounts = useWorkspaceMailAccounts(workspaceId);

  const messages = useSWR<MailMessageInfo[], GraphQLError>(
    workspaceId && accountId && loadMessages
      ? [
          'mailMessages',
          workspaceId,
          accountId,
          folder,
          folderId,
          virtualFolder,
          messageLimit,
          normalizedMessageSearch,
        ]
      : null,
    async () => {
      return fetchMailMessages(graphql, {
        workspaceId: workspaceId as string,
        accountId: accountId as string,
        folder,
        folderId,
        virtualFolder,
        messageLimit,
        normalizedMessageSearch,
      });
    },
    { suspense: false, keepPreviousData: true }
  );

  const accountMessages = useSWR<MailMessageInfo[], GraphQLError>(
    workspaceId && accountId && loadMessages
      ? ['mailAccountConversationMessages', workspaceId, accountId]
      : null,
    async () =>
      fetchMailAccountMessages(graphql, {
        workspaceId: workspaceId as string,
        accountId: accountId as string,
      }),
    { suspense: false, keepPreviousData: true }
  );

  const virtualFolders = useSWR<MailVirtualFolderInfo[], GraphQLError>(
    workspaceId && accountId
      ? ['mailVirtualFolders', workspaceId, accountId]
      : null,
    async () => {
      const response = await graphql.gql({
        query: mailVirtualFoldersQuery,
        variables: {
          workspaceId: workspaceId as string,
          accountId: accountId as string,
        },
      });
      return response.mailVirtualFolders;
    },
    { suspense: false }
  );

  const folders = useSWR<MailMailboxFolderInfo[], GraphQLError>(
    workspaceId ? ['mailFolders', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailFoldersQuery,
        variables: {
          workspaceId: workspaceId as string,
          accountId: null,
        },
      });
      return response.mailFolders;
    },
    { suspense: false }
  );

  const syncStates = useSWR<MailSyncStateInfo[], GraphQLError>(
    workspaceId ? ['mailSyncStates', workspaceId, accountId ?? null] : null,
    async () => {
      const response = await graphql.gql({
        query: mailSyncStatesQuery,
        variables: {
          workspaceId: workspaceId as string,
          accountId: accountId ?? null,
        },
      });
      return response.mailSyncStates;
    },
    { suspense: false }
  );

  const trustedSenders = useSWR<MailTrustedSenderInfo[], GraphQLError>(
    workspaceId ? ['mailTrustedSenders', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailTrustedSendersQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return response.mailTrustedSenders;
    },
    { suspense: false }
  );

  const rules = useSWR<MailRuleInfo[], GraphQLError>(
    workspaceId && accountId ? ['mailRules', workspaceId, accountId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailRulesQuery,
        variables: {
          workspaceId: workspaceId as string,
          accountId: accountId as string,
        },
      });
      return response.mailRules;
    },
    { suspense: false }
  );

  const signatures = useSWR<MailSignatureInfo[], GraphQLError>(
    workspaceId ? ['mailSignatures', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailSignaturesQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return response.mailSignatures;
    },
    { suspense: false }
  );

  const signatureAssignments = useSWR<
    MailSignatureAssignmentInfo[],
    GraphQLError
  >(
    workspaceId ? ['mailSignatureAssignments', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailSignatureAssignmentsQuery,
        variables: {
          workspaceId: workspaceId as string,
          accountId: null,
        },
      });
      return response.mailSignatureAssignments;
    },
    { suspense: false }
  );

  const autoresponders = useSWR<MailAutoresponderInfo[], GraphQLError>(
    workspaceId ? ['mailAutoresponders', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailAutorespondersQuery,
        variables: {
          workspaceId: workspaceId as string,
          accountId: null,
        },
      });
      return response.mailAutoresponders;
    },
    { suspense: false }
  );

  const selectedBody = useSWR<MailMessageBodyInfo | null, GraphQLError>(
    workspaceId &&
      accountId &&
      selectedMessageId &&
      accounts.data?.some(account => account.id === accountId)
      ? [
          'mailMessageBody',
          workspaceId,
          selectedMessageId,
          accountId,
          folder,
          folderId,
          virtualFolder,
        ]
      : null,
    async () => {
      try {
        const response = await graphql.gql({
          query: mailMessageBodyQuery,
          variables: {
            workspaceId: workspaceId as string,
            messageId: selectedMessageId as string,
          },
        });
        return response.mailMessageBody;
      } catch (error) {
        if (
          error instanceof Error &&
          error.message.includes('Mail message not found')
        ) {
          void accounts.mutate().catch(() => undefined);
          return null;
        }
        throw error;
      }
    },
    { suspense: false }
  );

  const mailboxRoot = useSWR<MailMailboxRootInfo, GraphQLError>(
    workspaceId && accounts.data && accounts.data.length > 0
      ? ['mailboxRoot', workspaceId]
      : null,
    async () => {
      const response = await graphql.gql({
        query: mailboxRootQuery,
        variables: { workspaceId: workspaceId as string },
      });
      accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
      return response.mailboxRoot;
    },
    { suspense: false }
  );

  const refreshMail = async () => {
    await accounts.mutate();
    await messages.mutate();
    await accountMessages.mutate();
    await virtualFolders.mutate();
    await folders.mutate();
    await rules.mutate();
    await signatures.mutate();
    await signatureAssignments.mutate();
    await autoresponders.mutate();
    await trustedSenders.mutate();
  };
  const refreshAfterAccountSync = () =>
    Promise.all([refreshMail(), syncStates.mutate()]);
  const refreshMailRef = useRef(refreshMail);
  useEffect(() => {
    refreshMailRef.current = refreshMail;
  });
  useEffect(() => {
    return subscribeMailAccountsChanged(event => {
      if (!workspaceId || event.workspaceId !== workspaceId) return;
      const remainingAccounts = (accounts.data ?? []).filter(
        account => account.id !== event.deletedAccountId
      );
      void accounts
        .mutate(remainingAccounts, false)
        .then(() => refreshMailRef.current())
        .catch(() => undefined);
    });
  }, [accounts, workspaceId]);
  useEffect(() => {
    const refreshAfterMailMutation = (mutation: OfflineMutationRecord) => {
      if (!workspaceId || mutation.workspaceId !== workspaceId) {
        return;
      }
      if (mutation.operation === 'mail.createFolder') {
        const payload = mutation.payload as MailCreateFolderMutationPayload;
        setOptimisticFolderCreates(previous => {
          const confirmedCreate = optimisticFolderCreateInfo(
            mutation.id,
            payload.input,
            folders.data,
            'confirming'
          );
          const existingIndex = previous.findIndex(
            create => create.id === mutation.id
          );
          if (existingIndex === -1) {
            return [...previous, confirmedCreate];
          }
          return previous.map(create =>
            create.id === mutation.id ? confirmedCreate : create
          );
        });
      }
      void refreshMailRef.current().catch(() => undefined);
    };
    const refreshAfterFailedMailMutation = (
      mutation: OfflineMutationRecord
    ) => {
      if (!workspaceId || mutation.workspaceId !== workspaceId) {
        return;
      }
      if (mutation.operation === 'mail.createFolder') {
        const payload = mutation.payload as MailCreateFolderMutationPayload;
        const failedCreate = optimisticFolderCreateInfo(
          mutation.id,
          payload.input,
          folders.data,
          'failed'
        );
        setOptimisticFolderCreates(previous =>
          previous.some(create => create.id === mutation.id)
            ? previous.map(create =>
                create.id === mutation.id ? failedCreate : create
              )
            : [...previous, failedCreate]
        );
      }
      void refreshMailRef.current().catch(() => undefined);
    };
    const completedSubscription = mailMutationOutbox.completed$.subscribe(
      refreshAfterMailMutation
    );
    const failedSubscription = mailMutationOutbox.failed$.subscribe(
      refreshAfterFailedMailMutation
    );
    return () => {
      completedSubscription.unsubscribe();
      failedSubscription.unsubscribe();
    };
  }, [folders.data, mailMutationOutbox, workspaceId]);
  useEffect(() => {
    const refreshCurrentScope = () =>
      Promise.all([
        messages.mutate(),
        accountMessages.mutate(),
        folders.mutate(),
        syncStates.mutate(),
      ]);
    const onMailChanged = (rawEvent: Event) => {
      const event = rawEvent as CustomEvent<MailChangedEventV1>;
      if (
        !workspaceId ||
        event.detail.workspaceId !== workspaceId ||
        (accountId && event.detail.accountId !== accountId)
      ) {
        return;
      }
      void refreshCurrentScope().catch(() => undefined);
    };
    const onReconnect = () => {
      if (workspaceId) {
        void refreshCurrentScope().catch(() => undefined);
      }
    };
    window.addEventListener(MAIL_CHANGED_BROWSER_EVENT, onMailChanged);
    window.addEventListener(MAIL_RECONNECTED_BROWSER_EVENT, onReconnect);
    return () => {
      window.removeEventListener(MAIL_CHANGED_BROWSER_EVENT, onMailChanged);
      window.removeEventListener(MAIL_RECONNECTED_BROWSER_EVENT, onReconnect);
    };
  }, [accountId, accountMessages, folders, messages, syncStates, workspaceId]);
  const schedulePostSyncRefreshes = () => {
    if (typeof window === 'undefined') return;
    for (const delay of MAIL_POST_SYNC_REFRESH_DELAYS_MS) {
      window.setTimeout(() => {
        void refreshAfterAccountSync().catch(() => undefined);
      }, delay);
    }
  };

  const { cachedValue: cachedAccounts } = useWorkspaceLocalCache<
    MailAccountInfo[]
  >({
    enabled: localCacheEnabled,
    namespace: 'mail',
    segment: 'accounts',
    value: accounts.data,
  });
  const { cachedValue: cachedFolders } = useWorkspaceLocalCache<
    MailMailboxFolderInfo[]
  >({
    enabled: localCacheEnabled,
    namespace: 'mail',
    segment: 'folders',
    value: folders.data,
  });
  const { cachedValue: cachedMailboxRoot } =
    useWorkspaceLocalCache<MailMailboxRootInfo>({
      enabled: localCacheEnabled,
      namespace: 'mail',
      segment: 'mailbox-root',
      value: mailboxRoot.data,
    });
  const currentMessages = getMailMessagesForSelection({
    messages: messages.data,
    accountId,
    folder,
    folderId,
    virtualFolder,
  });
  const { cachedValue: cachedMessages } = useWorkspaceLocalCache<
    MailMessageInfo[]
  >({
    enabled: localCacheEnabled,
    namespace: 'mail',
    segment: getMailMessagesLocalCacheSegment({
      accountId,
      folder,
      folderId,
      virtualFolder,
      limit: messageLimit,
      search: normalizedMessageSearch,
    }),
    value: currentMessages,
  });
  const currentCachedMessages = getMailMessagesForSelection({
    messages: cachedMessages,
    accountId,
    folder,
    folderId,
    virtualFolder,
  });
  const { cachedValue: cachedSelectedBody } =
    useWorkspaceLocalCache<MailMessageBodyInfo>({
      enabled: Boolean(localCacheEnabled && selectedMessageId),
      namespace: 'mail',
      segment: getMailMessageBodyLocalCacheSegment(selectedMessageId),
      value: selectedBody.data ?? undefined,
    });
  const useOfflineAccountsFallback = isBackendUnavailableError(accounts.error);
  const useOfflineFoldersFallback = isBackendUnavailableError(folders.error);
  const useOfflineMessagesFallback = isBackendUnavailableError(messages.error);
  const useOfflineSelectedBodyFallback = isBackendUnavailableError(
    selectedBody.error
  );
  const useOfflineMailboxRootFallback =
    isBackendUnavailableError(mailboxRoot.error) || useOfflineAccountsFallback;
  const effectiveAccounts =
    accounts.data ?? (useOfflineAccountsFallback ? cachedAccounts : undefined);
  const canonicalFolders =
    folders.data ?? (useOfflineFoldersFallback ? cachedFolders : undefined);
  const effectiveFolders = useMemo(
    () =>
      applyMailFolderMutationProjection(
        canonicalFolders,
        mailMutationProjections,
        mailMutationRecords
      ),
    [canonicalFolders, mailMutationProjections, mailMutationRecords]
  );
  const canonicalMessages =
    currentMessages ??
    (useOfflineMessagesFallback ? currentCachedMessages : undefined);
  const effectiveMessages = useMemo(() => {
    const projected = applyMailMessageMutationProjection(
      canonicalMessages,
      effectiveFolders,
      mailMutationProjections,
      mailMutationRecords
    );
    return filterMailMessagesForSelection({
      messages: projected,
      accountId,
      folder,
      folderId,
      virtualFolder,
    });
  }, [
    accountId,
    canonicalMessages,
    effectiveFolders,
    folder,
    folderId,
    mailMutationProjections,
    mailMutationRecords,
    virtualFolder,
  ]);
  const effectiveAccountMessages = useMemo(
    () =>
      applyMailMessageMutationProjection(
        accountMessages.data,
        effectiveFolders,
        mailMutationProjections,
        mailMutationRecords
      ),
    [
      accountMessages.data,
      effectiveFolders,
      mailMutationProjections,
      mailMutationRecords,
    ]
  );
  const effectiveSelectedBody =
    selectedBody.data ??
    (useOfflineSelectedBodyFallback ? cachedSelectedBody : undefined);
  const effectiveMailboxRoot =
    mailboxRoot.data ??
    (useOfflineMailboxRootFallback ? cachedMailboxRoot : undefined);
  useEffect(() => {
    const outboxCreates = mailMutationProjections.flatMap(
      (projection): PendingMailFolderCreateInfo[] => {
        const mutation = mailMutationRecords.find(
          record => record.id === projection.id
        );
        if (
          !mutation ||
          mutation.workspaceId !== workspaceId ||
          mutation.operation !== 'mail.createFolder'
        ) {
          return [];
        }
        const payload = mutation.payload as MailCreateFolderMutationPayload;
        return [
          optimisticFolderCreateInfo(
            mutation.id,
            payload.input,
            effectiveFolders,
            projection.syncState
          ),
        ];
      }
    );
    if (outboxCreates.length === 0) return;
    setOptimisticFolderCreates(previous => {
      const next = [...previous];
      for (const outboxCreate of outboxCreates) {
        const existingIndex = next.findIndex(
          create => create.id === outboxCreate.id
        );
        if (existingIndex === -1) {
          next.push(outboxCreate);
        } else {
          next[existingIndex] = outboxCreate;
        }
      }
      return next;
    });
  }, [
    effectiveFolders,
    mailMutationProjections,
    mailMutationRecords,
    workspaceId,
  ]);
  useEffect(() => {
    if (!effectiveFolders) return;
    setOptimisticFolderCreates(previous =>
      previous.filter(create => {
        return !effectiveFolders.some(
          folder =>
            folder.accountId === create.accountId && folder.path === create.path
        );
      })
    );
  }, [effectiveFolders]);
  const pendingFolderCreates = optimisticFolderCreates;
  const pendingMailMutations = mailMutationProjections
    .filter(projection => projection.syncState === 'pending')
    .map(mailOutboxMutationInfo);
  const failedMailMutations = mailMutationProjections
    .filter(projection => projection.syncState === 'failed')
    .map(mailOutboxMutationInfo);
  const folderMutationStates = useMemo(
    () =>
      new Map(
        Array.from(latestMailFolderMutationStates(mailMutationProjections)).map(
          ([folderId, projection]) =>
            [folderId, mailOutboxMutationInfo(projection)] as const
        )
      ),
    [mailMutationProjections]
  );
  const messageMutationStates = useMemo(
    () =>
      new Map(
        Array.from(
          latestMailMessageMutationStates(mailMutationProjections)
        ).map(
          ([messageId, projection]) =>
            [messageId, mailOutboxMutationInfo(projection)] as const
        )
      ),
    [mailMutationProjections]
  );

  const moveMessages = async (messageIds: string[], targetFolderId: string) => {
    if (!workspaceId) return;
    const uniqueMessageIds = Array.from(new Set(messageIds)).filter(Boolean);
    if (uniqueMessageIds.length === 0) return;
    const previousMessages = messages.data ?? effectiveMessages;
    const optimisticMessages = previousMessages?.filter(
      message => !uniqueMessageIds.includes(message.id)
    );
    if (optimisticMessages) {
      await messages.mutate(optimisticMessages, false);
    }
    try {
      await mailMutationOutbox.enqueueMoveMessages(
        workspaceId,
        uniqueMessageIds,
        targetFolderId
      );
    } catch (error) {
      if (optimisticMessages) {
        await messages.mutate(previousMessages, false);
      }
      throw error;
    }
  };

  return {
    accounts: effectiveAccounts,
    accountsLoading: accounts.isLoading && effectiveAccounts === undefined,
    accountsError: accounts.error,
    messages: effectiveMessages,
    messagesLoading: messages.isLoading && effectiveMessages === undefined,
    messagesError: messages.error,
    accountMessages: effectiveAccountMessages,
    accountMessagesLoading: accountMessages.isLoading,
    accountMessagesError: accountMessages.error,
    virtualFolders: virtualFolders.data,
    virtualFoldersLoading: virtualFolders.isLoading,
    virtualFoldersError: virtualFolders.error,
    folders: effectiveFolders,
    foldersLoading: folders.isLoading && effectiveFolders === undefined,
    foldersError: folders.error,
    syncStates: syncStates.data,
    syncStatesLoading: syncStates.isLoading,
    syncStatesError: syncStates.error,
    trustedSenders: trustedSenders.data,
    trustedSendersLoading: trustedSenders.isLoading,
    trustedSendersError: trustedSenders.error,
    rules: rules.data,
    rulesLoading: rules.isLoading,
    rulesError: rules.error,
    signatures: signatures.data,
    signaturesLoading: signatures.isLoading,
    signaturesError: signatures.error,
    signatureAssignments: signatureAssignments.data,
    signatureAssignmentsLoading: signatureAssignments.isLoading,
    signatureAssignmentsError: signatureAssignments.error,
    autoresponders: autoresponders.data,
    autorespondersLoading: autoresponders.isLoading,
    autorespondersError: autoresponders.error,
    selectedBody: effectiveSelectedBody,
    selectedBodyMissing: selectedBody.data === null,
    selectedBodyLoading:
      selectedBody.isLoading && effectiveSelectedBody === undefined,
    selectedBodyError: selectedBody.error,
    mailboxRoot: effectiveMailboxRoot,
    mailboxRootLoading:
      mailboxRoot.isLoading && effectiveMailboxRoot === undefined,
    mailboxRootError: mailboxRoot.error,
    pendingFolderCreates,
    pendingMailMutations,
    failedMailMutations,
    folderMutationStates,
    messageMutationStates,
    refreshAccounts: accounts.mutate,
    refreshMessages: messages.mutate,
    refreshAccountMessages: accountMessages.mutate,
    refreshFolders: folders.mutate,
    refreshSyncStates: syncStates.mutate,
    refreshTrustedSenders: trustedSenders.mutate,
    refreshVirtualFolders: virtualFolders.mutate,
    refreshRules: rules.mutate,
    refreshSignatures: signatures.mutate,
    refreshSignatureAssignments: signatureAssignments.mutate,
    refreshAutoresponders: autoresponders.mutate,
    refreshBody: selectedBody.mutate,
    async connectAccount(input) {
      const response = await graphql.gql({
        query: connectMailAccountMutation,
        variables: { input },
      });
      const connectedAccount = response.connectMailAccount;
      const currentAccounts = accounts.data ?? [];
      const optimisticAccounts = currentAccounts.some(
        account => account.id === connectedAccount.id
      )
        ? currentAccounts.map(account =>
            account.id === connectedAccount.id ? connectedAccount : account
          )
        : [...currentAccounts, connectedAccount];
      await accounts.mutate(optimisticAccounts, false);
      void refreshMail().catch(() => undefined);
      void mailboxRoot.mutate().catch(() => undefined);
      void accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
      return connectedAccount;
    },
    async updateAccount(input) {
      const response = await graphql.gql({
        query: updateMailAccountMutation,
        variables: { input },
      });
      await refreshMail();
      return response.updateMailAccount;
    },
    async deleteAccount(accountIdToDelete) {
      if (!workspaceId) return;
      await graphql.gql({
        query: deleteMailAccountMutation,
        variables: { workspaceId, accountId: accountIdToDelete },
      });
      await workspaceLocalCache.clearNamespace('mail');
      await deleteMailRemoteImageCacheEntriesForWorkspace(workspaceId);
      await refreshMail();
      broadcastMailAccountsChanged({
        workspaceId,
        deletedAccountId: accountIdToDelete,
      });
    },
    async syncAccount(accountIdToSync) {
      if (!workspaceId) return '';
      const response = await graphql.gql({
        query: syncMailAccountMutation,
        variables: { workspaceId, accountId: accountIdToSync },
      });
      await refreshAfterAccountSync();
      schedulePostSyncRefreshes();
      return response.syncMailAccount;
    },
    async setWatchInterest(accountId, interest) {
      if (!workspaceId) return;
      await graphql.gql({
        query: setMailWatchInterestMutation,
        variables: { workspaceId, accountId, interest },
      });
    },
    async credentialShareProposals(input) {
      const response = await graphql.gql({
        query: mailCredentialShareProposalsQuery,
        variables: { input },
      });
      return response.mailCredentialShareProposals;
    },
    async applyManagedCredentialGrant(input) {
      if (!workspaceId) return;
      await requestAppResourceJson(
        buildMailboxAppResourceUrl(
          workspaceId,
          input.accountId,
          '/credential-grants'
        ),
        {
          body: JSON.stringify({
            accessDocId: input.accessDocId ?? null,
            role: input.role,
            targetId: input.targetId,
            targetType: input.targetType,
          }),
          method: 'POST',
        }
      );
      await accounts.mutate();
    },
    async listManagedCredentialGrants(accountId) {
      if (!workspaceId) return [];
      const response = await requestAppResourceJson<{
        grants: MailManagedCredentialGrantInfo[];
      }>(
        buildMailboxAppResourceUrl(workspaceId, accountId, '/credential-grants')
      );
      return response.grants;
    },
    async revokeManagedCredentialGrant(input) {
      if (!workspaceId) return;
      await requestAppResourceJson(
        buildMailboxAppResourceUrl(
          workspaceId,
          input.accountId,
          '/credential-grants/revoke'
        ),
        {
          body: JSON.stringify({
            targetId: input.targetId,
            targetType: input.targetType,
          }),
          method: 'POST',
        }
      );
      await accounts.mutate();
    },
    async setMailboxScope(input) {
      if (!workspaceId) return;
      await requestAppResourceJson(
        buildMailboxAppResourceUrl(workspaceId, input.accountId, '/scope'),
        {
          body: JSON.stringify({
            areaRootFolderId: input.areaRootFolderId ?? null,
            scope: input.scope,
          }),
          method: 'POST',
        }
      );
      await accounts.mutate();
      await accessibleFolderCache.fetchAccessibleFolders();
    },
    async sendMail(input) {
      await mailMutationOutbox.enqueueSend(input);
    },
    async saveDraft(input) {
      const response = await graphql.gql({
        query: saveMailDraftMutation,
        variables: { input },
      });
      await Promise.all([
        messages.mutate(),
        accountMessages.mutate(),
        folders.mutate(),
      ]);
      return response.saveMailDraft;
    },
    async sendSavedDraft(input) {
      await graphql.gql({
        query: sendSavedMailDraftMutation,
        variables: { input },
      });
      const refreshedAccountMessages = await fetchMailAccountMessages(graphql, {
        workspaceId: input.workspaceId,
        accountId: accountId as string,
      });
      await accountMessages.mutate(refreshedAccountMessages, false);
      await Promise.all([
        messages.mutate(),
        folders.mutate(),
        selectedBody.mutate(),
      ]);
    },
    async prepareComposition(input) {
      const response = await graphql.gql({
        query: prepareMailCompositionQuery,
        variables: { input },
      });
      return response.prepareMailComposition;
    },
    async upsertSignature(input) {
      const response = await graphql.gql({
        query: upsertMailSignatureMutation,
        variables: { input },
      });
      await signatures.mutate();
      await signatureAssignments.mutate();
      return response.upsertMailSignature;
    },
    async deleteSignature(signatureId) {
      if (!workspaceId) return;
      await graphql.gql({
        query: deleteMailSignatureMutation,
        variables: { workspaceId, signatureId },
      });
      await signatures.mutate();
      await signatureAssignments.mutate();
      await autoresponders.mutate();
    },
    async assignSignature(input) {
      const response = await graphql.gql({
        query: assignMailSignatureMutation,
        variables: { input },
      });
      await signatureAssignments.mutate();
      return response.assignMailSignature;
    },
    async deleteSignatureAssignment(accountIdToDelete, scope) {
      if (!workspaceId) return;
      await graphql.gql({
        query: deleteMailSignatureAssignmentMutation,
        variables: {
          workspaceId,
          accountId: accountIdToDelete,
          scope,
        },
      });
      await signatureAssignments.mutate();
    },
    async upsertAutoresponder(input) {
      const response = await graphql.gql({
        query: upsertMailAutoresponderMutation,
        variables: { input },
      });
      await autoresponders.mutate();
      return response.upsertMailAutoresponder;
    },
    async deleteAutoresponderOverride(accountIdToDelete) {
      if (!workspaceId) return;
      await graphql.gql({
        query: deleteMailAutoresponderOverrideMutation,
        variables: {
          workspaceId,
          accountId: accountIdToDelete,
        },
      });
      await autoresponders.mutate();
    },
    async createFolder(input) {
      const id = nanoid();
      setOptimisticFolderCreates(previous => [
        ...previous,
        optimisticFolderCreateInfo(id, input, effectiveFolders),
      ]);
      try {
        await mailMutationOutbox.enqueueCreateFolder(input, id);
      } catch (error) {
        setOptimisticFolderCreates(previous =>
          previous.filter(create => create.id !== id)
        );
        throw error;
      }
    },
    async updateFolder(input) {
      await mailMutationOutbox.enqueueUpdateFolderAndWait(
        input,
        MAIL_FOLDER_PROVIDER_CONFIRMATION_TIMEOUT_MS
      );
      await folders.mutate();
    },
    async deleteFolder(folderIdToDelete) {
      if (!workspaceId) return;
      await mailMutationOutbox.enqueueDeleteFolder(
        workspaceId,
        folderIdToDelete
      );
    },
    async updateMessageState(input) {
      const previousMessages = messages.data ?? effectiveMessages;
      const previousVirtualFolders = virtualFolders.data;
      const selection = { accountId, folder, folderId, virtualFolder };
      const previousMessage = previousMessages?.find(
        message => message.id === input.messageId
      );
      const optimisticMessages = patchMailMessages(
        previousMessages,
        input,
        selection
      );
      const optimisticVirtualFolders = patchMailVirtualFolderCounts(
        previousVirtualFolders,
        previousMessage,
        input
      );
      if (optimisticMessages) {
        await messages.mutate(optimisticMessages, false);
      }
      if (optimisticVirtualFolders) {
        await virtualFolders.mutate(optimisticVirtualFolders, false);
      }
      try {
        await mailMutationOutbox.enqueueUpdateMessageState(input);
      } catch (error) {
        if (optimisticMessages) {
          await messages.mutate(previousMessages, false);
        }
        if (optimisticVirtualFolders) {
          await virtualFolders.mutate(previousVirtualFolders, false);
        }
        throw error;
      }
    },
    async trustSender(senderAddress) {
      if (!workspaceId) return undefined;
      const response = await graphql.gql({
        query: trustMailSenderMutation,
        variables: {
          input: {
            workspaceId,
            senderAddress,
          },
        },
      });
      await trustedSenders.mutate();
      return response.trustMailSender;
    },
    moveMessages,
    async moveMessage(messageId, targetFolderId) {
      await moveMessages([messageId], targetFolderId);
    },
    async retryFailedMailMutation(id) {
      await mailMutationOutbox.retryFailed(id);
    },
    async dismissFailedMailMutation(id) {
      await mailMutationOutbox.removeFailed(id);
      setOptimisticFolderCreates(previous =>
        previous.filter(create => create.id !== id)
      );
    },
    async upsertRule(input) {
      await graphql.gql({
        query: upsertMailRuleMutation,
        variables: { input },
      });
      await rules.mutate();
    },
    async deleteRule(ruleId) {
      if (!workspaceId) return;
      await graphql.gql({
        query: deleteMailRuleMutation,
        variables: { workspaceId, ruleId },
      });
      await rules.mutate();
    },
  };
}

export function useMailMailboxRoot(workspaceId: string | null): {
  mailboxRoot: MailMailboxRootInfo | undefined;
  isLoading: boolean;
  error: GraphQLError | undefined;
  refresh: SWRResponse<MailMailboxRootInfo, GraphQLError>['mutate'];
} {
  const graphql = useService(GraphQLService);
  const mailboxRoot = useSWR<MailMailboxRootInfo, GraphQLError>(
    workspaceId ? ['mailboxRoot', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailboxRootQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return response.mailboxRoot;
    },
    { suspense: false }
  );

  return {
    mailboxRoot: mailboxRoot.data,
    isLoading: mailboxRoot.isLoading,
    error: mailboxRoot.error,
    refresh: mailboxRoot.mutate,
  };
}

export function useWorkspaceMailAdmin(workspaceId: string | null): {
  overview: MailAdminOverviewInfo | undefined;
  isLoading: boolean;
  error: GraphQLError | undefined;
  refresh: SWRResponse<MailAdminOverviewInfo, GraphQLError>['mutate'];
  updatePolicy: (input: UpdateMailPolicyInput) => Promise<void>;
  updateCacheUserOverride: (
    input: UpdateMailCacheUserOverrideInput
  ) => Promise<void>;
  resetCacheUserOverride: (userId: string) => Promise<void>;
  rebuildSearchIndex: (
    limit?: number,
    accountId?: string | null
  ) => Promise<{
    jobId: string;
    status: string;
    queuedMessages: number;
    skippedMessages: number;
  } | null>;
} {
  const graphql = useService(GraphQLService);
  const key = workspaceId ? ['mailAdminOverview', workspaceId] : null;
  const overview = useSWR<MailAdminOverviewInfo, GraphQLError>(
    key,
    async () => {
      const response = await graphql.gql({
        query: mailAdminOverviewQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return response.mailAdminOverview;
    },
    { suspense: false }
  );

  return {
    overview: overview.data,
    isLoading: overview.isLoading,
    error: overview.error,
    refresh: overview.mutate,
    async updatePolicy(input) {
      if (!workspaceId) return;
      await graphql.gql({
        query: updateMailPolicyMutation,
        variables: { workspaceId, input },
      });
      await overview.mutate();
    },
    async updateCacheUserOverride(input) {
      if (!workspaceId) return;
      await graphql.gql({
        query: updateMailCacheUserOverrideMutation,
        variables: { input },
      });
      await overview.mutate();
    },
    async resetCacheUserOverride(userId) {
      if (!workspaceId) return;
      await graphql.gql({
        query: resetMailCacheUserOverrideMutation,
        variables: { workspaceId, userId },
      });
      await overview.mutate();
    },
    async rebuildSearchIndex(limit = 500, accountId = null) {
      if (!workspaceId) return null;
      const response = await graphql.gql({
        query: rebuildMailSearchIndexMutation,
        variables: { input: { workspaceId, accountId, limit } },
      });
      await overview.mutate();
      return response.rebuildMailSearchIndex;
    },
  };
}

export function useCurrentUserMailQuota(workspaceId: string | null): {
  quota: MailUserQuotaInfo | undefined;
  isLoading: boolean;
  error: GraphQLError | undefined;
  refresh: SWRResponse<MailUserQuotaInfo, GraphQLError>['mutate'];
  rebuildAccountSearchIndex: (
    accountId: string,
    limit?: number
  ) => Promise<RebuildMailSearchIndexInfo | null>;
} {
  const graphql = useService(GraphQLService);
  const quota = useSWR<MailUserQuotaInfo, GraphQLError>(
    workspaceId ? ['mailCurrentUserQuota', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailCurrentUserQuotaQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return response.mailCurrentUserQuota;
    },
    {
      suspense: false,
      refreshInterval: latestQuota => {
        const coverage = latestQuota?.factors.find(
          factor => factor.kind === 'search_window_coverage'
        );
        if (!coverage?.limitCount) return 0;
        return (coverage.usedCount ?? 0) < coverage.limitCount ? 5_000 : 0;
      },
    }
  );

  return {
    quota: quota.data,
    isLoading: quota.isLoading,
    error: quota.error,
    refresh: quota.mutate,
    async rebuildAccountSearchIndex(accountId, limit = 500) {
      if (!workspaceId) return null;
      const response = await graphql.gql({
        query: rebuildMailSearchIndexMutation,
        variables: { input: { workspaceId, accountId, limit } },
      });
      await quota.mutate();
      return response.rebuildMailSearchIndex;
    },
  };
}

export function useMailSearchMaintenance(workspaceId: string | null): {
  diagnostics: MailSearchDiagnosticsInfo | undefined;
  isLoading: boolean;
  error: GraphQLError | undefined;
  refresh: SWRResponse<MailSearchDiagnosticsInfo, GraphQLError>['mutate'];
  reindex: (
    input?: Omit<MailSearchReindexInput, 'workspaceId'>
  ) => Promise<MailSearchReindexResultInfo | null>;
} {
  const graphql = useService(GraphQLService);
  const diagnostics = useSWR<MailSearchDiagnosticsInfo, GraphQLError>(
    workspaceId ? ['mailSearchDiagnostics', workspaceId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailSearchDiagnosticsQuery,
        variables: {
          id: workspaceId as string,
          input: { limit: 10 },
        },
      });
      return response.workspace.mailSearchDiagnostics;
    },
    { suspense: false }
  );

  return {
    diagnostics: diagnostics.data,
    isLoading: diagnostics.isLoading,
    error: diagnostics.error,
    refresh: diagnostics.mutate,
    async reindex(input = {}) {
      if (!workspaceId) return null;
      const response = await graphql.gql({
        query: reindexMailSearchMutation,
        variables: {
          input: {
            ...input,
            workspaceId,
          },
        },
      });
      await diagnostics.mutate();
      return response.reindexMailSearch;
    },
  };
}
